import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [role, setRole] = useState(params.get("role") === "trainer" ? "trainer" : "learner");
  const [refCode, setRefCode] = useState((params.get("ref") || "").toUpperCase());
  const [refInfo, setRefInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refCode && refCode.length >= 4) {
      import("../lib/api").then(({ api }) =>
        api.get(`/referral/validate/${refCode}`).then(r => setRefInfo(r.data)).catch(() => setRefInfo(null))
      );
    } else {
      setRefInfo(null);
    }
  }, [refCode]);

  // Instructors must be 18+, learners 16+. Mirrored server-side, which is
  // the authority — this just avoids a pointless round trip.
  const minAge = role === "trainer" ? 18 : 16;
  const ageFrom = (iso) => {
    if (!iso) return null;
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const before = t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate());
    return before ? a - 1 : a;
  };
  const age = ageFrom(dob);
  const tooYoung = age !== null && age < minAge;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setErr("You must agree to the Terms of Service and Privacy Policy to create an account.");
      return;
    }
    if (tooYoung) {
      setErr(`You must be at least ${minAge} years old to register as a ${role}.`);
      return;
    }
    setLoading(true); setErr("");
    try {
      const u = await register({ name, email, password, phone, role, date_of_birth: dob, referral_code: refCode || undefined, terms_accepted: true });
      toast.success(`Welcome, ${u.name}`);
      nav(role === "trainer" ? "/trainer" : "/learner", { replace: true });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 grid-bg">
      <Seo
        title="Create Your DriveTerrain Account – Learner or Instructor"
        description="Sign up in 60 seconds to book driving lessons or list yourself as a verified driving instructor across Canada. Free to join, CAD payouts."
        keywords="driving instructor signup, learner sign up, become a driving instructor canada, list driving school"
        canonical="/register"
      />
      <div className="w-full max-w-md bg-card border border-border p-8" data-testid="register-card">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Create account</div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Get started</h1>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label>I am a</Label>
            <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2 mt-2">
              <label className={`border ${role==='learner'?'border-primary bg-primary/5':'border-border'} p-3 cursor-pointer flex items-center gap-2 rounded-sm`}>
                <RadioGroupItem value="learner" data-testid="role-learner" />
                <span className="text-sm font-medium">Learner</span>
              </label>
              <label className={`border ${role==='trainer'?'border-primary bg-primary/5':'border-border'} p-3 cursor-pointer flex items-center gap-2 rounded-sm`}>
                <RadioGroupItem value="trainer" data-testid="role-trainer" />
                <span className="text-sm font-medium">Trainer</span>
              </label>
            </RadioGroup>
          </div>
          <div>
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="reg-name" className="rounded-sm" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="reg-email" className="rounded-sm" />
          </div>
          <div>
            <Label>Date of birth</Label>
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required
                   max={new Date().toISOString().slice(0, 10)}
                   className="rounded-sm" data-testid="reg-dob" />
            <div className={`text-xs mt-1.5 ${tooYoung ? "text-destructive" : "text-muted-foreground"}`} data-testid="reg-dob-hint">
              {tooYoung
                ? `You must be at least ${minAge} to register as a ${role} — this date makes you ${age}.`
                : `${role === "trainer" ? "Instructors" : "Learners"} must be ${minAge} or older.`}
            </div>
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="reg-phone" className="rounded-sm" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reg-password" className="rounded-sm" />
          </div>
          <div>
            <Label>Referral code (optional)</Label>
            <Input value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} placeholder="e.g. ABC12345" className="rounded-sm font-mono tracking-widest" data-testid="reg-ref-code" />
            {refInfo?.valid && (
              <div className="text-xs text-primary mt-1.5" data-testid="ref-valid">
                ✓ Referred by {refInfo.referrer_name} — you&apos;ll get ${refInfo.bonus_amount} off your first booking
              </div>
            )}
            {refCode && refInfo && !refInfo.valid && (
              <div className="text-xs text-muted-foreground mt-1.5" data-testid="ref-invalid">Code not recognized</div>
            )}
          </div>
          <label
            htmlFor="signup-consent"
            className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer select-none transition-colors ${
              consent
                ? "border-primary/40 bg-primary/5"
                : err
                ? "border-destructive/60 bg-destructive/5"
                : "border-border hover:border-primary/30"
            }`}
            data-testid="signup-consent-label"
          >
            <Checkbox
              id="signup-consent"
              checked={consent}
              onCheckedChange={(v) => { setConsent(!!v); if (v) setErr(""); }}
              className="mt-0.5"
              data-testid="signup-consent"
            />
            <span className="text-sm text-foreground leading-relaxed">
              I agree to DriveTerrain&apos;s{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline text-primary font-medium" data-testid="signup-terms-link" onClick={(e) => e.stopPropagation()}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-primary font-medium" data-testid="signup-privacy-link" onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          <Button type="submit" className="w-full rounded-sm" disabled={loading || !consent || tooYoung} data-testid="reg-submit">
            {loading ? "Creating..." : tooYoung ? `Must be ${minAge}+ to continue` : consent ? "Create account" : "Check the box above to continue"}
          </Button>
          {err && <div className="text-sm text-destructive" data-testid="reg-error">{err}</div>}
        </form>
        <div className="mt-6 text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="link-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
